import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { format, parseISO } from "date-fns"
import { DataTable, GRID_COLOR, InlineBar, Legend, MUTED_COLOR, Panel, WORKING_COLOR, column, row_tooltip } from "../trade_review/trade_review_parts"
import { fmt_int, fmt_signed_pct, fmt_usd2, fmt_usd_short } from "../trade_review/trade_review_model"
import { READABLE, fmt_date_range, kroger_deal, kroger_item, promo_lift_review } from "./promo_lift_review_model"
import { ReadPill } from "./promo_lift_review_parts"

function readable(row: kroger_deal): boolean {
  return row.read === READABLE
}

const DEAL_COLUMNS: column<kroger_deal>[] = [
  { header: "Billed as", left: true, cell: (row: kroger_deal) => row.promotion },
  { header: "Dates", left: true, cell: (row: kroger_deal) => fmt_date_range(row.start, row.end) },
  { header: "Scan/unit", cell: (row: kroger_deal) => fmt_usd2(row.rate) },
  { header: "Divisions", cell: (row: kroger_deal) => fmt_int(row.divisions) },
  { header: "Base/day", cell: (row: kroger_deal) => fmt_int(row.base_day) },
  { header: "Deal/day", cell: (row: kroger_deal) => fmt_int(row.deal_day) },
  { header: "Lift", cell: (row: kroger_deal) => (readable(row) ? fmt_signed_pct(row.lift) : "") },
  { header: "Units billed", cell: (row: kroger_deal) => fmt_int(row.units_paid) },
  { header: "Extra units", cell: (row: kroger_deal) => (readable(row) ? fmt_int(row.extra_units) : "") },
  { header: "Scan cost", cell: (row: kroger_deal) => fmt_usd_short(row.spend) },
  { header: "Cost/extra unit", cell: (row: kroger_deal) => (readable(row) ? fmt_usd2(row.per_extra) : "") },
  { header: "Read", left: true, cell: (row: kroger_deal) => <ReadPill read={row.read} /> },
]

const ITEM_COLUMNS = (top: number): column<kroger_item>[] => [
  { header: "Item (Kroger's name)", left: true, cell: (row: kroger_item) => row.item },
  { header: "Base/day", cell: (row: kroger_item) => fmt_int(row.base_day) },
  { header: "Lift", left: true, cell: (row: kroger_item) => <InlineBar share={row.lift / top} label={fmt_signed_pct(row.lift)} /> },
  { header: "Units billed", cell: (row: kroger_item) => fmt_int(row.units_paid) },
]

export function KrogerTab({ data }: { data: promo_lift_review }) {
  const deals = data.kroger_deals.filter(readable).map((row: kroger_deal) => ({ ...row, label: format(parseISO(row.start), "MMM yyyy") }))
  const top = Math.max(...data.kroger_items.map((row: kroger_item) => row.lift))
  return (
    <div class="space-y-6">
      <Panel title="Kroger deals: units a day before and during" note="Kroger's EDLP scan pays on every unit sold, so it counts sales all period. A deal's units a day against the rest of its EDLP period is its lift. All divisions together.">
        <div class="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={deals} margin={{ top: 20, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid vertical={false} stroke={GRID_COLOR} />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={fmt_int} width={52} tick={{ fontSize: 12 }} />
              <Tooltip content={row_tooltip((row: kroger_deal) => [fmt_date_range(row.start, row.end), row.promotion, `Base ${fmt_int(row.base_day)} a day`, `Deal ${fmt_int(row.deal_day)} a day`, `Lift ${fmt_signed_pct(row.lift)}`]) as never} />
              <Bar dataKey="base_day" fill={MUTED_COLOR} radius={[2, 2, 0, 0]} maxBarSize={32} isAnimationActive={false} />
              <Bar dataKey="deal_day" fill={WORKING_COLOR} radius={[2, 2, 0, 0]} maxBarSize={32} isAnimationActive={false}>
                <LabelList dataKey="lift" position="top" formatter={(value: number) => fmt_signed_pct(value)} fontSize={11} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <Legend items={[{ label: "Base units a day", color: MUTED_COLOR }, { label: "Units a day on deal", color: WORKING_COLOR }]} />
      </Panel>
      <Panel title="Each Kroger deal" note="Baseline incomplete means the EDLP period around the deal is still open or covered few stores, so its lift is not read.">
        <DataTable columns={DEAL_COLUMNS} rows={data.kroger_deals} row_key={(row: kroger_deal) => row.deal} />
      </Panel>
      <Panel title="Kroger lift by item, readable deals" note="Items with at least 2,000 units billed. Every entrée more than doubled on BOGO; Crossroads pastas lifted least.">
        <DataTable columns={ITEM_COLUMNS(top)} rows={data.kroger_items} row_key={(row: kroger_item) => row.item} />
      </Panel>
    </div>
  )
}
